/**
 * Partial implementation of siunitx based on the spec here:
 * http://texdoc.net/texmf-dist/doc/latex/siunitx/siunitx.pdf
 *
 * Only a few of the macros, below, are implemented from that doc.
 * However, all units, prefixes, abbreviations, etc are supported.
 *
 * Only `per-mode=symbol` is implemented from the spec; all other options are ignored.
 * EXCEPT, a custom `per-mode=pedagogical` option, which puts *all* units following
 * a `\per` directive into the denominator. Additional `\per` direcives are ignored.
 * If you wish to use pedagogical mode *with* symbol mode, just use the `/` character directly.
 *
 * Eg.
 * ```
 * \si{\kilo\joule\per\kilo\gram\kelvin} -> kJ K kg^{-1}
 * \si{\kilo\joule\per\kilo\gram\per\kelvin} -> kJ kg^{-1} K^{-1}
 * \si[per-mode=symbol]{\kilo\joule\per\kilo\gram\kelvin} -> kJ K/kg
 * \si[per-mode=symbol]{\kilo\joule\per\kilo\gram\per\kelvin} -> kJ/(kg K)
 * \si{\kilo\joule / \kilo\gram\kelvin} -> kJ/kg K
 * \si[per-mode=pedagogical]{\kilo\joule\per\kilo\gram\kelvin} -> kJ kg^{-1} K^{-1}
 * ```
 */

const katex = require('katex');

// The macros defined here are the only ones implemented.

/** \si[option]{unit} */
katex.__defineMacro('\\si', siParser);
/** \SI[option]{number}[preunit]{unit} */
katex.__defineMacro('\\SI', SIParser);
/** \num[option]{number} */
katex.__defineMacro('\\num', numParser);
/** \ang[option]{number} */
katex.__defineMacro('\\ang', angParser);
/** \tothe{number} */
katex.__defineMacro('\\tothe', '^{#1}');

/** \numcomma[option]{number}
 * This is a Brilliant extension that works like \num, but separates digit triples with commas.
 */
katex.__defineMacro('\\numcomma', numCommaParser);

// Separate triples of numbers with a "narrow space".
const SPACER_SPACE = '\\,';
// Separate triples of numbers with a comma.
const SPACER_COMMA = '{,}';

function siParser(context) {
  let args = parseArgs(context, 2);
  checkArgs(args, 1, 2);

  // First argument is optional, and sets some flags.
  let opts = {};
  if (args[0].optional === true) {
    opts = parseOpts(args[0]);
    args = args.slice(1);
  }
  checkArgs(args, 1);

  // Second argument is the unit.
  const latex = parseUnits(args[0], opts);

  return latex;
}

function SIParser(context) {
  let args = parseArgs(context, 4);
  checkArgs(args, 2, 4);

  // First argument is optional, and sets some flags.
  let opts = {};
  if (args[0].optional === true) {
    opts = parseOpts(args[0]);
    args = args.slice(1);
  }
  checkArgs(args, 2);

  // Second argument is interpreted as a number.
  const number = parseNumbers(args[0]);
  args = args.slice(1);
  checkArgs(args, 1);

  let latex = '';
  // Third argument is an optional preunit.
  latex += `${parseUnits(args[0], opts)}`;
  args = args.slice(1);

  // The fourth argument is the unit, or, if there is no fourth, then the third one was the unit.
  if (args.length != 0) {
    latex += ` ${number}\\ ${parseUnits(args[0], opts)}`;
  } else {
    latex = `${number}\\ ${latex}`;
  }

  return latex;
}

function numParser(context, spacer = SPACER_SPACE) {
  let args = parseArgs(context, 2);
  checkArgs(args, 1, 2);

  // First argument is optional, and sets some flags. Which we ignore.
  let opts = {};
  if (args[0].optional === true) {
    opts = parseOpts(args[0]);
    args = args.slice(1);
  }
  checkArgs(args, 1);

  // Second argument is interpreted as a number.
  const latex = parseNumbers(args[0], spacer);

  return `{ ${latex} }`;
}

function numCommaParser(context) {
  return numParser(context, SPACER_COMMA);
}

function angParser(context) {
  let args = parseArgs(context, 2);
  checkArgs(args, 1, 2);

  // First argument is optional, and sets some flags. Which we ignore.
  let opts = {};
  if (args[0].optional === true) {
    opts = parseOpts(args[0]);
    args = args.slice(1);
  }
  checkArgs(args, 1);

  // Second argument is interpreted as a number.
  const latex = `${parseNumbers(args[0])} \\degree`;

  return latex;
}

function checkArgs(args, min, max = Infinity) {
  if (args.length < min || args.length > max) {
    throw `Unexpected number of arguments.`;
  }
}

function parseArgs(context, maxArgs) {
  let arg = [];
  let args = [];
  let depth = 0;
  let token = context.future();
  while (token && token.text != 'EOF') {
    if (args.length > maxArgs) {
      throw `Unexpected argument.`;
    }
    token = context.future();
    switch (token.text) {
      case '[': // FALL-THROUGH
      case '{':
        if (depth != 0) {
          arg.push({ token: token.text, type: TOKEN_TYPES.LITERAL });
        } else {
          arg.optional = token.text == '[';
        }
        ++depth;
        break;
      case ']': // FALL-THROUGH
      case '}':
        --depth;
        if (depth < 0) {
          return args;
        } else if (depth != 0) {
          arg.push({ token: token.text, type: TOKEN_TYPES.LITERAL });
        } else {
          args.push(arg);
          arg = [];
        }
        break;
      case '\\num': // Swallow nested \\num tokens.
        if (depth == 0) {
          return args;
        }
        break;
      default:
        if (depth == 0) {
          return args;
        } else {
          arg.push(matchKnownToken(token.text));
        }
        break;
    }

    context.popToken();
  }

  return args;
}

function parseOpts(opts) {
  const opt = opts.map(c => c.token).join('');
  const split = opt.split('=');
  if (split.length != 2) {
    throw `Unexpected option.`;
  }
  return {
    [split[0].trim()]: split[1].trim().toLowerCase(),
  };
}

function spaceTriplets(run, dec, spacer) {
  if (run.length < 4) {
    return run;
  }

  if (!dec) {
    run.reverse();
  } else {
    // If we are spacing with a comma, do not space after the decimal point.
    if (spacer === SPACER_COMMA) {
      return run;
    }
  }
  const spaced = [];
  for (let i = 0; i < run.length; ++i) {
    if (i != 0 && i % 3 == 0) {
      spaced.push(spacer);
    }
    spaced.push(run[i]);
  }
  return dec ? spaced : spaced.reverse();
}

function parseNumbers(input, spacer = SPACER_SPACE) {
  let exp = false;
  let dec = false;
  let run = [];
  const output = [];
  input.forEach(num => {
    if (num.token.length > 1) {
      output.push(num.token);
      return;
    }
    if (num.token === ',') {
      // Ignore.
      return;
    }
    const code = num.token.charCodeAt(0);
    if (code >= 48 && code <= 57) {
      // [0-9]
      run.push(num.token);
    } else {
      if (run.length != 0) {
        output.push(spaceTriplets(run, dec, spacer).join(''));
        run = [];
      }
      switch (num.token) {
        case 'e':
        case 'E':
          exp = true;
          if (output.length != 0) {
            output.push('\\times');
          }
          output.push('10^{');
          break;
        case '.':
          dec = true;
        // FALL-THROUGH
        default:
          output.push(num.token);
          break;
      }
    }
  });

  if (run.length != 0) {
    output.push(spaceTriplets(run, dec, spacer).join(''));
  }

  if (exp) {
    output.push('}');
  }

  return output.join(' ');
}

class UnitIterator {
  constructor(unitTokens) {
    this.tokens = unitTokens;
    this.idx = -1;
  }

  peek() {
    const idx = this.idx + 1;
    return idx < this.tokens.length ? this.tokens[idx] : null;
  }

  remember() {
    const idx = this.idx - 1;
    return idx >= 0 ? this.tokens[idx] : null;
  }

  next() {
    const token = this.peek();
    if (token) {
      ++this.idx;
    }
    return token;
  }
}

function parseUnits(tokens, opts) {
  const numerator = [];
  const denominator = [];
  const iter = new UnitIterator(tokens);

  const perSymbol = opts['per-mode'] === 'symbol';
  const pedagogical = opts['per-mode'] === 'pedagogical';
  let per = false;
  let cur = iter.next();
  while (cur) {
    let text = '';
    let next = null;
    switch (cur.type) {
      case TOKEN_TYPES.PER:
        per = true;
        cur = iter.next();
        continue;
      case TOKEN_TYPES.PREFIX:
        next = iter.next();
        if (!next || (next.type != TOKEN_TYPES.UNIT && next.type != TOKEN_TYPES.LITERAL)) {
          throw `Unit expected after prefix "${cur.token}" but got "${next.token}".`;
        }
        text += cur.text === false ? cur.token : `\\text{${cur.token}}`;
        cur = next;
      // FALL-THROUGH
      case TOKEN_TYPES.UNIT:
        text += cur.text === false ? cur.token : `\\text{${cur.token}}`;
        next = iter.peek();
        let space = true;
        if (next && next.type == TOKEN_TYPES.POWER_SUFFIX) {
          if (!per || perSymbol) {
            text += `^{${next.power}}`;
          } else {
            text += `^{${next.power * -1}}`;
          }
          space = false;
          cur = iter.next();
        } else if (per) {
          if (!perSymbol) {
            text += '^{-1}';
            space = false;
          }
        }
        next = iter.peek();

        if (!next) {
          space = false;
        } else if (next.type == TOKEN_TYPES.PER) {
          space = space && (!perSymbol || per);
        } else if (numOrSym(next.token)) {
          space = false;
        } else if (cur.token === 'W' && next.token === 'h') {
          space = false;
        }

        if (space) {
          text += '\\,';
        }
        break;
      case TOKEN_TYPES.POWER_SUFFIX:
        text = `^{${cur.power}}`;
        break;
      case TOKEN_TYPES.LITERAL:
        if (cur.token === '.') {
          const next = iter.peek();
          const prev = iter.remember();
          if (numOrSym(next.token) && numOrSym(prev.token)) {
            text = cur.token;
          } else {
            text = '\\,';
          }
        } else if (cur.text === false || cur.token.startsWith('\\') || numOrSym(cur.token)) {
          text = cur.token;
        } else {
          text = `\\text{${cur.token}}`;
          let next = iter.peek();
          while (next && next.type == TOKEN_TYPES.LITERAL && !numOrSym(next.token)) {
            cur = iter.next();
            text += `\\text{${cur.token}}`;
            next = iter.peek();
          }
          if (next && next.type == TOKEN_TYPES.POWER_SUFFIX) {
            if (!per || perSymbol) {
              text += `^{${next.power}}`;
            } else {
              text += `^{${next.power * -1}}`;
            }
            cur = iter.next();
          } else if (per) {
            if (!perSymbol) {
              text += '^{-1}';
            }
          }
        }
        break;
      default:
        text = cur.token;
        break;
    }

    if (per) {
      denominator.push(text);
      next = iter.peek();
      if (next && next.type != TOKEN_TYPES.LITERAL && !pedagogical) {
        per = false;
      }
    } else {
      numerator.push(text);
    }
    cur = iter.next();
  }

  const num = numerator.join(' ');
  let den;
  if (perSymbol && denominator.length > 1) {
    den = `(${denominator.join(' ')})`;
  } else {
    den = denominator.join(' ');
  }
  return `${num} ${perSymbol && den.length != 0 ? '/' : ''} ${den}`;
}

function matchKnownToken(text) {
  return KNOWN_TOKENS[text] || { token: text, type: TOKEN_TYPES.LITERAL };
}

function numOrSym(token) {
  if (!token || token.length != 1) {
    return false;
  }
  const c = token.charCodeAt(0);
  return c <= 64 || (c >= 91 && c <= 96) || c >= 123;
}

const TOKEN_TYPES = {
  LITERAL: 0,
  UNIT: 1,
  PREFIX: 2,
  POWER_SUFFIX: 3,
  PER: 4,
};

const KNOWN_TOKENS = {
  // Units.
  '\\ampere': { token: 'A', type: TOKEN_TYPES.UNIT },
  '\\pA': { token: 'pA', type: TOKEN_TYPES.UNIT },
  '\\nA': { token: 'nA', type: TOKEN_TYPES.UNIT },
  '\\uA': { token: '\\mu\\text{A}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mA': { token: 'mA', type: TOKEN_TYPES.UNIT },
  '\\A': { token: 'A', type: TOKEN_TYPES.UNIT },
  '\\kA': { token: 'kA', type: TOKEN_TYPES.UNIT },
  '\\candela': { token: 'cd', type: TOKEN_TYPES.UNIT },
  '\\gram': { token: 'g', type: TOKEN_TYPES.UNIT },
  '\\kelvin': { token: 'K', type: TOKEN_TYPES.UNIT },
  '\\K': { token: 'K', type: TOKEN_TYPES.UNIT },
  '\\kilogram': { token: 'kg', type: TOKEN_TYPES.UNIT },
  '\\fg': { token: 'fg', type: TOKEN_TYPES.UNIT },
  '\\pg': { token: 'pg', type: TOKEN_TYPES.UNIT },
  '\\ng': { token: 'ng', type: TOKEN_TYPES.UNIT },
  '\\ug': { token: '\\mu\\text{g}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mg': { token: 'mg', type: TOKEN_TYPES.UNIT },
  '\\g': { token: 'g', type: TOKEN_TYPES.UNIT },
  '\\kg': { token: 'kg', type: TOKEN_TYPES.UNIT },
  '\\metre': { token: 'm', type: TOKEN_TYPES.UNIT },
  '\\meter': { token: 'm', type: TOKEN_TYPES.UNIT },
  '\\pm': { token: 'pm', type: TOKEN_TYPES.UNIT },
  '\\nm': { token: 'nm', type: TOKEN_TYPES.UNIT },
  '\\um': { token: '\\mu\\text{m}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mm': { token: 'mm', type: TOKEN_TYPES.UNIT },
  '\\cm': { token: 'cm', type: TOKEN_TYPES.UNIT },
  '\\dm': { token: 'dm', type: TOKEN_TYPES.UNIT },
  '\\m': { token: 'm', type: TOKEN_TYPES.UNIT },
  '\\km': { token: 'km', type: TOKEN_TYPES.UNIT },
  '\\mole': { token: 'mol', type: TOKEN_TYPES.UNIT },
  '\\fmol': { token: 'fmol', type: TOKEN_TYPES.UNIT },
  '\\pmol': { token: 'pmol', type: TOKEN_TYPES.UNIT },
  '\\nmol': { token: 'nmol', type: TOKEN_TYPES.UNIT },
  '\\umol': { token: '\\mu\\text{mol}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mmol': { token: 'mmol', type: TOKEN_TYPES.UNIT },
  '\\mol': { token: 'mol', type: TOKEN_TYPES.UNIT },
  '\\kmol': { token: 'kmol', type: TOKEN_TYPES.UNIT },
  '\\second': { token: 's', type: TOKEN_TYPES.UNIT },
  '\\as': { token: 'as', type: TOKEN_TYPES.UNIT },
  '\\fs': { token: 'fs', type: TOKEN_TYPES.UNIT },
  '\\ps': { token: 'ps', type: TOKEN_TYPES.UNIT },
  '\\ns': { token: 'ns', type: TOKEN_TYPES.UNIT },
  '\\us': { token: '\\mu\\text{s}', type: TOKEN_TYPES.UNIT, text: false },
  '\\ms': { token: 'ms', type: TOKEN_TYPES.UNIT },
  '\\s': { token: 's', type: TOKEN_TYPES.UNIT },

  '\\becquerel': { token: 'Bq', type: TOKEN_TYPES.UNIT },
  '\\degreeCelsius': {
    token: '\\hspace{0.6mm}^{\\circ}\\hspace{-0.2mm}\\text{C}',
    type: TOKEN_TYPES.UNIT,
    text: false,
  },
  '\\celsius': {
    token: '\\hspace{0.6mm}^{\\circ}\\hspace{-0.2mm}\\text{C}',
    type: TOKEN_TYPES.UNIT,
    text: false,
  },
  '\\coulomb': { token: 'C', type: TOKEN_TYPES.UNIT },
  '\\farad': { token: 'F', type: TOKEN_TYPES.UNIT },
  '\\pF': { token: 'pF', type: TOKEN_TYPES.UNIT },
  '\\fF': { token: 'fF', type: TOKEN_TYPES.UNIT },
  '\\F': { token: 'F', type: TOKEN_TYPES.UNIT },
  '\\gray': { token: 'Gy', type: TOKEN_TYPES.UNIT },
  '\\hertz': { token: 'Hz', type: TOKEN_TYPES.UNIT },
  '\\mHz': { token: 'mHz', type: TOKEN_TYPES.UNIT },
  '\\Hz': { token: 'Hz', type: TOKEN_TYPES.UNIT },
  '\\kHz': { token: 'kHz', type: TOKEN_TYPES.UNIT },
  '\\MHz': { token: 'MHz', type: TOKEN_TYPES.UNIT },
  '\\GHz': { token: 'GHz', type: TOKEN_TYPES.UNIT },
  '\\THz': { token: 'THz', type: TOKEN_TYPES.UNIT },
  '\\henry': { token: 'H', type: TOKEN_TYPES.UNIT },
  '\\joule': { token: 'J', type: TOKEN_TYPES.UNIT },
  '\\uJ': { token: '\\mu\\text{J}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mJ': { token: 'mJ', type: TOKEN_TYPES.UNIT },
  '\\J': { token: 'J', type: TOKEN_TYPES.UNIT },
  '\\kJ': { token: 'kJ', type: TOKEN_TYPES.UNIT },
  '\\katal': { token: 'kat', type: TOKEN_TYPES.UNIT },
  '\\lumen': { token: 'lm', type: TOKEN_TYPES.UNIT },
  '\\lux': { token: 'lx', type: TOKEN_TYPES.UNIT },
  '\\newton': { token: 'N', type: TOKEN_TYPES.UNIT },
  '\\mN': { token: 'mN', type: TOKEN_TYPES.UNIT },
  '\\N': { token: 'N', type: TOKEN_TYPES.UNIT },
  '\\kN': { token: 'kN', type: TOKEN_TYPES.UNIT },
  '\\MN': { token: 'MN', type: TOKEN_TYPES.UNIT },
  '\\ohm': { token: '\\Omega', type: TOKEN_TYPES.UNIT, text: false },
  '\\mohm': { token: '\\text{m}\\Omega', type: TOKEN_TYPES.UNIT, text: false },
  '\\kohm': { token: '\\text{k}\\Omega', type: TOKEN_TYPES.UNIT, text: false },
  '\\Mohm': { token: '\\text{M}\\Omega', type: TOKEN_TYPES.UNIT, text: false },
  '\\pascal': { token: 'Pa', type: TOKEN_TYPES.UNIT },
  '\\Pa': { token: 'Pa', type: TOKEN_TYPES.UNIT },
  '\\kPa': { token: 'kPa', type: TOKEN_TYPES.UNIT },
  '\\MPa': { token: 'MPa', type: TOKEN_TYPES.UNIT },
  '\\GPa': { token: 'GPa', type: TOKEN_TYPES.UNIT },
  '\\radian': { token: 'rad', type: TOKEN_TYPES.UNIT },
  '\\siemens': { token: 'S', type: TOKEN_TYPES.UNIT },
  '\\sievert': { token: 'Sv', type: TOKEN_TYPES.UNIT },
  '\\steradian': { token: 'sr', type: TOKEN_TYPES.UNIT },
  '\\tesla': { token: 'T', type: TOKEN_TYPES.UNIT },
  '\\volt': { token: 'V', type: TOKEN_TYPES.UNIT },
  '\\pV': { token: 'pV', type: TOKEN_TYPES.UNIT },
  '\\nV': { token: 'nV', type: TOKEN_TYPES.UNIT },
  '\\uV': { token: '\\mu\\text{V}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mV': { token: 'mV', type: TOKEN_TYPES.UNIT },
  '\\V': { token: 'V', type: TOKEN_TYPES.UNIT },
  '\\kV': { token: 'kV', type: TOKEN_TYPES.UNIT },
  '\\watt': { token: 'W', type: TOKEN_TYPES.UNIT },
  '\\uW': { token: '\\mu\\text{W}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mW': { token: 'mW', type: TOKEN_TYPES.UNIT },
  '\\W': { token: 'W', type: TOKEN_TYPES.UNIT },
  '\\kW': { token: 'kW', type: TOKEN_TYPES.UNIT },
  '\\MW': { token: 'MW', type: TOKEN_TYPES.UNIT },
  '\\GW': { token: 'GW', type: TOKEN_TYPES.UNIT },
  '\\kWh': { token: 'kWh', type: TOKEN_TYPES.UNIT },
  '\\weber': { token: 'Wb', type: TOKEN_TYPES.UNIT },

  '\\day': { token: 'd', type: TOKEN_TYPES.UNIT },
  '\\degree': { token: '\\degree', type: TOKEN_TYPES.UNIT, text: false },
  '\\hectare': { token: 'ha', type: TOKEN_TYPES.UNIT },
  '\\hour': { token: 'h', type: TOKEN_TYPES.UNIT },
  '\\litre': { token: 'l', type: TOKEN_TYPES.UNIT },
  '\\liter': { token: 'L', type: TOKEN_TYPES.UNIT },
  '\\ul': { token: '\\mu\\text{l}', type: TOKEN_TYPES.UNIT, text: false },
  '\\ml': { token: 'ml', type: TOKEN_TYPES.UNIT },
  '\\l': { token: 'l', type: TOKEN_TYPES.UNIT },
  '\\hl': { token: 'hl', type: TOKEN_TYPES.UNIT },
  '\\uL': { token: '\\mu\\text{L}', type: TOKEN_TYPES.UNIT, text: false },
  '\\mL': { token: 'mL', type: TOKEN_TYPES.UNIT },
  '\\L': { token: 'L', type: TOKEN_TYPES.UNIT },
  '\\hL': { token: 'hL', type: TOKEN_TYPES.UNIT },
  '\\minute': { token: 'min', type: TOKEN_TYPES.UNIT },
  '\\arcminute': { token: '\\prime', type: TOKEN_TYPES.UNIT, text: false },
  '\\arcsecond': { token: '\\prime\\prime', type: TOKEN_TYPES.UNIT, text: false },
  '\\tonne': { token: 't', type: TOKEN_TYPES.UNIT },

  '\\astronomicalunit': { token: 'au', type: TOKEN_TYPES.UNIT },
  '\\atomicmassunit': { token: 'u', type: TOKEN_TYPES.UNIT },
  '\\amu': { token: 'u', type: TOKEN_TYPES.UNIT },
  '\\bohr': { token: 'a_0', type: TOKEN_TYPES.UNIT, text: false },
  '\\clight': { token: 'c_0', type: TOKEN_TYPES.UNIT, text: false },
  '\\dalton': { token: 'Da', type: TOKEN_TYPES.UNIT },
  '\\electronmass': { token: 'm_e', type: TOKEN_TYPES.UNIT, text: false },
  '\\electronvolt': { token: 'eV', type: TOKEN_TYPES.UNIT },
  '\\meV': { token: 'meV', type: TOKEN_TYPES.UNIT },
  '\\eV': { token: 'eV', type: TOKEN_TYPES.UNIT },
  '\\keV': { token: 'keV', type: TOKEN_TYPES.UNIT },
  '\\MeV': { token: 'MeV', type: TOKEN_TYPES.UNIT },
  '\\GeV': { token: 'GeV', type: TOKEN_TYPES.UNIT },
  '\\TeV': { token: 'TeV', type: TOKEN_TYPES.UNIT },
  '\\elementarycharge': { token: 'e', type: TOKEN_TYPES.UNIT, text: false },
  '\\hartree': { token: 'E_h', type: TOKEN_TYPES.UNIT, text: false },
  '\\planckbar': { token: '\\hbar', type: TOKEN_TYPES.UNIT, text: false },

  '\\angstrom': { token: '\\AA', type: TOKEN_TYPES.UNIT, text: true },
  '\\bar': { token: 'bar', type: TOKEN_TYPES.UNIT },
  '\\barn': { token: 'b', type: TOKEN_TYPES.UNIT },
  '\\bel': { token: 'B', type: TOKEN_TYPES.UNIT },
  '\\decibel': { token: 'dB', type: TOKEN_TYPES.UNIT },
  '\\dB': { token: 'dB', type: TOKEN_TYPES.UNIT },
  '\\knot': { token: 'kn', type: TOKEN_TYPES.UNIT },
  '\\mmHg': { token: 'mmHg', type: TOKEN_TYPES.UNIT },
  '\\nauticalmile': { token: 'M', type: TOKEN_TYPES.UNIT },
  '\\neper': { token: 'Np', type: TOKEN_TYPES.UNIT },

  // Prefixes.
  '\\yocto': { token: 'y', type: TOKEN_TYPES.PREFIX },
  '\\zepto': { token: 'z', type: TOKEN_TYPES.PREFIX },
  '\\atto': { token: 'a', type: TOKEN_TYPES.PREFIX },
  '\\femto': { token: 'f', type: TOKEN_TYPES.PREFIX },
  '\\pico': { token: 'p', type: TOKEN_TYPES.PREFIX },
  '\\nano': { token: 'n', type: TOKEN_TYPES.PREFIX },
  '\\micro': { token: '\\mu', type: TOKEN_TYPES.PREFIX, text: false },
  '\\milli': { token: 'm', type: TOKEN_TYPES.PREFIX },
  '\\centi': { token: 'c', type: TOKEN_TYPES.PREFIX },
  '\\deci': { token: 'd', type: TOKEN_TYPES.PREFIX },
  '\\deca': { token: 'da', type: TOKEN_TYPES.PREFIX },
  '\\deka': { token: 'da', type: TOKEN_TYPES.PREFIX },
  '\\hecto': { token: 'h', type: TOKEN_TYPES.PREFIX },
  '\\kilo': { token: 'k', type: TOKEN_TYPES.PREFIX },
  '\\mega': { token: 'M', type: TOKEN_TYPES.PREFIX },
  '\\giga': { token: 'G', type: TOKEN_TYPES.PREFIX },
  '\\tera': { token: 'T', type: TOKEN_TYPES.PREFIX },
  '\\peta': { token: 'P', type: TOKEN_TYPES.PREFIX },
  '\\exa': { token: 'E', type: TOKEN_TYPES.PREFIX },
  '\\zetta': { token: 'Z', type: TOKEN_TYPES.PREFIX },
  '\\yotta': { token: 'Y', type: TOKEN_TYPES.PREFIX },

  // Powers.
  '\\squared': { power: 2, type: TOKEN_TYPES.POWER_SUFFIX },
  '\\cubed': { power: 3, type: TOKEN_TYPES.POWER_SUFFIX },

  // Per.
  '\\per': { token: '/', type: TOKEN_TYPES.PER },
};
